
import { supabase } from "@/integrations/supabase/client";

// Helper utilities for referral functionality

// Generate a referral URL for a user
export const generateReferralUrl = (userId?: string) => {
  if (!userId) return '';
  
  // ALWAYS redirect the special ID to our destination URL
  if (userId === '21d11b63-7b91-46fa-8c91-a30744574195') {
    console.log("generateReferralUrl: Special ID detected, using direct redirect URL");
    return 'https://www.omooduduwa.org/join';
  }
  
  // Use the official website URL instead of the Lovable app URL
  const baseUrl = 'https://www.omooduduwa.org';
  const referralUrl = `${baseUrl}/join?referrer=${encodeURIComponent(userId)}`;
  console.log("Generated referral URL:", referralUrl); 
  return referralUrl;
};

// Get a user's referral URL (keeping this for backwards compatibility)
export const getUserReferralUrl = (userId: string) => {
  return generateReferralUrl(userId);
};

/**
 * Check if a specific referrer ID needs special handling
 */
export const checkSpecialReferrerId = (referrerId: string | null): string | null => {
  if (!referrerId) return null;
  
  // Check for special cases that need redirection
  if (referrerId === '21d11b63-7b91-46fa-8c91-a30744574195') {
    console.log("checkSpecialReferrerId: Special ID detected, returning redirect URL");
    return 'https://www.omooduduwa.org/join';
  }
  
  return null;
};

// Extract referrer ID from URL parameters and handle special cases
export const extractReferrerId = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const referrerId = urlParams.get('referrer');
  const currentUrl = window.location.href;
  
  // Always check the full URL for the special pattern
  if (currentUrl.includes('21d11b63-7b91-46fa-8c91-a30744574195')) {
    console.log("extractReferrerId: Special pattern in URL, redirecting...");
    window.location.replace('https://www.omooduduwa.org/join');
    return null;
  }
  
  // Also check for the special referrer ID
  if (referrerId === '21d11b63-7b91-46fa-8c91-a30744574195') {
    console.log("extractReferrerId: Special referrer ID detected, redirecting...");
    window.location.replace('https://www.omooduduwa.org/join');
    return null;
  }
  
  return referrerId;
};

// Get badge level based on referral count
export const getBadgeLevel = (referralCount: number) => {
  if (referralCount >= 5000) return { level: 'legend', label: 'Legend' };
  if (referralCount >= 1000) return { level: 'platinum', label: 'Platinum' };
  if (referralCount >= 500) return { level: 'gold', label: 'Gold' };
  if (referralCount >= 100) return { level: 'silver', label: 'Silver' };
  if (referralCount >= 1) return { level: 'bronze', label: 'Bronze' };
  return { level: 'none', label: 'No Badge' };
};

// Fetch both approved and pending referral counts for a user
export const fetchReferralCounts = async (userId: string): Promise<{approved: number, pending: number}> => {
  try {
    // Get approved referrals
    const { data: approvedData, error: approvedError } = await supabase
      .from('community_members')
      .select('id')
      .eq('referred_by', userId)
      .eq('approved', true);
    
    if (approvedError) throw approvedError;
    
    // Get pending referrals
    const { data: pendingData, error: pendingError } = await supabase
      .from('community_members')
      .select('id')
      .eq('referred_by', userId)
      .eq('approved', false);
    
    if (pendingError) throw pendingError;
    
    return {
      approved: approvedData?.length || 0,
      pending: pendingData?.length || 0
    };
  } catch (error) {
    console.error('Error fetching referral counts:', error);
    return { approved: 0, pending: 0 };
  }
};

// Fetch just the referral count from the user's profile
export const fetchReferralCount = async (userId: string): Promise<number> => {
  try {
    // Use the database function to get the accurate count
    const { data, error } = await supabase
      .rpc('get_referral_count', { user_id: userId });
    
    if (error) {
      console.error('Error fetching referral count with RPC:', error);
      
      // Fallback to direct query
      const { data: directData, error: directError } = await supabase
        .from('community_members')
        .select('invite_count')
        .eq('id', userId)
        .single();
      
      if (directError) throw directError;
      return directData?.invite_count || 0;
    }
    
    return data || 0;
  } catch (error) {
    console.error('Error fetching referral count:', error);
    return 0;
  }
};

// Force update of invite count for a user based on actual referrals
export const updateInviteCount = async (userId: string): Promise<boolean> => {
  try {
    // Get actual count of approved referrals
    const { data: approvedData, error: countError } = await supabase
      .from('community_members')
      .select('id')
      .eq('referred_by', userId)
      .eq('approved', true);
    
    if (countError) throw countError;
    
    const actualCount = approvedData?.length || 0;
    
    // Update the user's invite_count to match
    const { error: updateError } = await supabase
      .from('community_members')
      .update({ invite_count: actualCount })
      .eq('id', userId);
    
    if (updateError) throw updateError;
    
    return true;
  } catch (error) {
    console.error('Error updating invite count:', error);
    return false;
  }
};

// Update user profile (nickname, avatar)
export const updateUserProfile = async (userId: string, updates: { nickname?: string, avatar_url?: string }) => {
  try {
    const { error } = await supabase
      .from('community_members')
      .update(updates)
      .eq('id', userId);
      
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating profile:', error);
    return { success: false, error };
  }
};

// Upload avatar image
export const uploadAvatar = async (userId: string, file: File) => {
  try {
    const fileExt = file.name.split('.').pop();
    const filePath = `avatars/${userId}.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('profiles')
      .upload(filePath, file, { upsert: true });
      
    if (uploadError) throw uploadError;
    
    const { data } = supabase.storage
      .from('profiles')
      .getPublicUrl(filePath);
      
    // Update user profile with new avatar URL
    await updateUserProfile(userId, { avatar_url: data.publicUrl });
    
    return { success: true, url: data.publicUrl };
  } catch (error) {
    console.error('Error uploading avatar:', error);
    return { success: false, error };
  }
};

// Sync referrer names for all members
export const syncReferrerNames = async (): Promise<boolean> => {
  try {
    console.log("Starting referrer names sync");
    
    // Get all members with referrers but no referrer_name
    const { data: membersToUpdate, error: fetchError } = await supabase
      .from('community_members')
      .select('id, referred_by')
      .is('referrer_name', null)
      .not('referred_by', 'is', null);
    
    if (fetchError) {
      console.error("Error fetching members to update:", fetchError);
      throw fetchError;
    }
    
    console.log(`Found ${membersToUpdate?.length || 0} members requiring referrer name updates`);
    
    if (!membersToUpdate || membersToUpdate.length === 0) {
      return true; // Nothing to update
    }
    
    // Update each member with the correct referrer name
    for (const member of membersToUpdate) {
      if (member.referred_by) {
        // Get the referrer's name
        const { data: referrer, error: referrerError } = await supabase
          .from('community_members')
          .select('nickname, full_name')
          .eq('id', member.referred_by)
          .single();
        
        if (referrerError) {
          console.error(`Error fetching referrer for ${member.id}:`, referrerError);
          continue;
        }
        
        if (referrer) {
          // Update the member with the referrer's name
          const referrerName = referrer.nickname || referrer.full_name;
          console.log(`Setting referrer name for member ${member.id} to "${referrerName}"`);
          
          const { error: updateError } = await supabase
            .from('community_members')
            .update({ referrer_name: referrerName })
            .eq('id', member.id);
          
          if (updateError) {
            console.error(`Error updating referrer_name for ${member.id}:`, updateError);
          }
        }
      }
    }
    
    console.log("Referrer names sync completed successfully");
    return true;
  } catch (error) {
    console.error('Error syncing referrer names:', error);
    return false;
  }
};
